import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Users,
  Gamepad2,
  Building2,
  Plus,
  Loader2,
  ChevronRight,
  DollarSign,
  Key,
  Target,
  Archive,
  ArchiveRestore
} from 'lucide-react';
import { campaignsAPI } from '../api/campaigns';
import AddCampaignModal from '../components/AddCampaignModal';

const Campaigns = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  const [view, setView] = useState('live');

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const filter = { view };
      if (statusFilter) filter.status = statusFilter;
      if (typeFilter) filter.type = typeFilter;
      const response = await campaignsAPI.getAll(filter);
      setCampaigns(response.data.campaigns);
    } catch (err) {
      setError('Failed to fetch campaigns');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveCampaign = async (campaign) => {
    const archiving = !campaign.archived;
    if (archiving && !window.confirm(`Archive "${campaign.name}"? It will be hidden from the live list (still fully accessible), and its scoped client portal will be archived too.`)) return;
    try {
      await campaignsAPI.archive(campaign.id, { archived: archiving, archivePortals: archiving });
      fetchCampaigns();
    } catch (err) {
      setError('Failed to archive campaign');
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [statusFilter, typeFilter, view]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return { bg: 'bg-green-500/15', text: 'text-green-400', border: 'border-green-500/25', dot: 'bg-green-400' };
      case 'planning': return { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/25', dot: 'bg-amber-400' };
      case 'completed': return { bg: 'bg-gray-500/15', text: 'text-white', border: 'border-gray-500/25', dot: 'bg-gray-400' };
      default: return { bg: 'bg-white/10', text: 'text-white', border: 'border-white', dot: 'bg-white/50' };
    }
  };

  const getTypeColor = (type) => {
    if (type === 'organic') return { bg: 'bg-purple-500/15', text: 'text-purple-400', border: 'border-purple-500/25' };
    if (type === 'sponsored_organic') return { bg: 'bg-indigo-500/15', text: 'text-indigo-400', border: 'border-indigo-500/25' };
    return { bg: 'bg-sc-pink/15', text: 'text-sc-pink', border: 'border-sc-pink/25' };
  };
  const typeLabel = (type) => type === 'organic' ? 'Organic' : type === 'sponsored_organic' ? 'Sponsored + Organic' : 'Sponsored';

  // Counts for filter badges
  const allCount = campaigns.length;
  const activeCount = campaigns.filter(c => c.status === 'active').length;
  const planningCount = campaigns.filter(c => c.status === 'planning').length;
  const completedCount = campaigns.filter(c => c.status === 'completed').length;
  const organicCount = campaigns.filter(c => c.type === 'organic').length;
  const sponsoredCount = campaigns.filter(c => c.type === 'sponsored').length;
  const combinedCount = campaigns.filter(c => c.type === 'sponsored_organic').length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a14 50%, #0a0a1a 100%)' }}>
        <div className="flex flex-col items-center">
          <Loader2 size={32} className="animate-spin text-sc-pink" />
          <span className="text-white mt-4 font-medium">Loading campaigns...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a14 50%, #0a0a1a 100%)' }}>
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">Campaigns</h1>
            <p className="text-sm text-white">Manage your sponsored and organic campaigns</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sc-pink/15 border border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 transition-all text-sm font-semibold"
          >
            <Plus className="w-4 h-4" />
            New Campaign
          </button>
        </div>

        {/* Filter badges */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {/* Live / Archived view toggle */}
          <div className="flex flex-wrap gap-1.5">
            {['live', 'archived', 'all'].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`text-[11px] px-2.5 py-1 rounded-lg capitalize transition-all ${
                  view === v
                    ? 'bg-sc-pink/20 text-sc-pink border border-sc-pink/35'
                    : 'bg-white/5 text-white border border-white hover:bg-white/10'
                }`}
              >
                {v}
              </button>
            ))}
          </div>

          <div className="w-px h-5 bg-white/10" />

          {/* Status filters */}
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setStatusFilter(null)}
              className={`text-[11px] px-2.5 py-1 rounded-lg transition-all ${
                statusFilter === null
                  ? 'bg-white/15 text-white border border-white'
                  : 'bg-white/5 text-white border border-white hover:bg-white/10'
              }`}
            >
              {allCount} total
            </button>
            {activeCount > 0 && (
              <button
                onClick={() => setStatusFilter(statusFilter === 'active' ? null : 'active')}
                className={`text-[11px] px-2.5 py-1 rounded-lg transition-all ${
                  statusFilter === 'active'
                    ? 'bg-green-500/25 text-green-400 border border-green-500/40'
                    : 'bg-green-500/10 text-green-400/70 border border-green-500/15 hover:bg-green-500/20'
                }`}
              >
                {activeCount} active
              </button>
            )}
            {planningCount > 0 && (
              <button
                onClick={() => setStatusFilter(statusFilter === 'planning' ? null : 'planning')}
                className={`text-[11px] px-2.5 py-1 rounded-lg transition-all ${
                  statusFilter === 'planning'
                    ? 'bg-amber-500/25 text-amber-400 border border-amber-500/40'
                    : 'bg-amber-500/10 text-amber-400/70 border border-amber-500/15 hover:bg-amber-500/20'
                }`}
              >
                {planningCount} planning
              </button>
            )}
            {completedCount > 0 && (
              <button
                onClick={() => setStatusFilter(statusFilter === 'completed' ? null : 'completed')}
                className={`text-[11px] px-2.5 py-1 rounded-lg transition-all ${
                  statusFilter === 'completed'
                    ? 'bg-gray-500/25 text-white border border-gray-500/40'
                    : 'bg-gray-500/10 text-white border border-gray-500/15 hover:bg-gray-500/20'
                }`}
              >
                {completedCount} completed
              </button>
            )}
          </div>

          <div className="w-px h-5 bg-white/10" />

          {/* Type filters */}
          <div className="flex flex-wrap gap-1.5">
            {organicCount > 0 && (
              <button
                onClick={() => setTypeFilter(typeFilter === 'organic' ? null : 'organic')}
                className={`text-[11px] px-2.5 py-1 rounded-lg transition-all ${
                  typeFilter === 'organic'
                    ? 'bg-purple-500/25 text-purple-400 border border-purple-500/40'
                    : 'bg-purple-500/10 text-purple-400/70 border border-purple-500/15 hover:bg-purple-500/20'
                }`}
              >
                {organicCount} organic
              </button>
            )}
            {sponsoredCount > 0 && (
              <button
                onClick={() => setTypeFilter(typeFilter === 'sponsored' ? null : 'sponsored')}
                className={`text-[11px] px-2.5 py-1 rounded-lg transition-all ${
                  typeFilter === 'sponsored'
                    ? 'bg-sc-pink/25 text-sc-pink border border-sc-pink/40'
                    : 'bg-sc-pink/10 text-sc-pink/70 border border-sc-pink/15 hover:bg-sc-pink/20'
                }`}
              >
                {sponsoredCount} sponsored
              </button>
            )}
            {combinedCount > 0 && (
              <button
                onClick={() => setTypeFilter(typeFilter === 'sponsored_organic' ? null : 'sponsored_organic')}
                className={`text-[11px] px-2.5 py-1 rounded-lg transition-all ${
                  typeFilter === 'sponsored_organic'
                    ? 'bg-indigo-500/25 text-indigo-400 border border-indigo-500/40'
                    : 'bg-indigo-500/10 text-indigo-400/70 border border-indigo-500/15 hover:bg-indigo-500/20'
                }`}
              >
                {combinedCount} sponsored + organic
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl mb-6 text-sm">
            {error}
          </div>
        )}

        {/* Empty State */}
        {campaigns.length === 0 && (
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <Target className="w-16 h-16 text-white/10 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No campaigns yet</h3>
              <p className="text-white mb-6 text-sm">Create your first campaign to start tracking influencer content</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-sc-pink/15 border border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 transition-all text-sm font-semibold"
              >
                <Plus className="w-4 h-4" />
                Create Your First Campaign
              </button>
            </div>
          </div>
        )}

        {/* Campaign cards */}
        {campaigns.length > 0 && (
          <div className="space-y-3">
            {campaigns.map((campaign) => {
              const statusStyle = getStatusColor(campaign.status);
              const typeStyle = getTypeColor(campaign.type);
              const creatorCount = campaign._count?.influencers || 0;
              const gameImage = campaign.game?.coverImageUrl || campaign.Game?.coverImageUrl;
              const gameTitle = campaign.game?.title || campaign.Game?.title;

              return (
                <div
                  key={campaign.id}
                  onClick={() => navigate(`/dashboard/campaigns/${campaign.id}`)}
                  className="bg-gray-900/80 border border-white rounded-xl p-5 cursor-pointer group transition-all duration-200 hover:border-white relative overflow-hidden"
                >
                  <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full opacity-5 blur-2xl" style={{ background: campaign.type === 'organic' ? '#8b5cf6' : campaign.type === 'sponsored_organic' ? '#6366f1' : '#ec4899' }} />

                  <div className="relative z-10 flex items-start gap-4">
                    {/* Game art */}
                    {gameImage ? (
                      <img
                        src={gameImage}
                        alt={gameTitle}
                        className="w-14 h-14 rounded-xl object-cover flex-shrink-0 ring-1 ring-white/10 group-hover:ring-sc-pink/30 transition-all"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-xl flex-shrink-0 bg-white/5 border border-white flex items-center justify-center group-hover:bg-white/8 transition-colors">
                        <Gamepad2 className="w-6 h-6 text-white/20" />
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2.5 mb-1.5">
                        <h3 className="text-base font-semibold text-white truncate group-hover:text-sc-pink transition-colors">
                          {campaign.name}
                        </h3>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] font-semibold rounded-full border flex-shrink-0 ${typeStyle.bg} ${typeStyle.text} ${typeStyle.border}`}>
                          {typeLabel(campaign.type)}
                        </span>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[10px] font-semibold rounded-full border flex-shrink-0 ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
                          {campaign.status?.charAt(0).toUpperCase() + campaign.status?.slice(1)}
                        </span>
                        {campaign.archived && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] font-semibold rounded-full border flex-shrink-0 bg-white/5 text-white border-white">
                            <Archive className="w-3 h-3" />
                            Archived
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-4 text-xs text-white">
                        {gameTitle && (
                          <span className="flex items-center gap-1.5">
                            <Gamepad2 className="w-3.5 h-3.5" />
                            {gameTitle}
                          </span>
                        )}
                        {campaign.clientName && (
                          <span className="flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5" />
                            {campaign.clientName}
                          </span>
                        )}
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(campaign.startDate)} - {formatDate(campaign.endDate)}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 mt-2.5">
                        <span className="flex items-center gap-1.5 text-xs text-white">
                          <Users className="w-3.5 h-3.5" />
                          <span className="font-medium text-white">{creatorCount}</span> creator{creatorCount !== 1 ? 's' : ''}
                        </span>
                        {(campaign.type === 'sponsored' || campaign.type === 'sponsored_organic') && campaign.budget > 0 && (
                          <span className="flex items-center gap-1.5 text-xs text-white">
                            <DollarSign className="w-3.5 h-3.5" />
                            <span className="font-medium text-white">{formatCurrency(campaign.budget)}</span>
                          </span>
                        )}
                        {(campaign.type === 'organic' || campaign.type === 'sponsored_organic') && campaign.keysAvailable > 0 && (
                          <span className="flex items-center gap-1.5 text-xs text-white">
                            <Key className="w-3.5 h-3.5" />
                            <span className="font-medium text-white">{campaign.keysAvailable}</span> keys
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right action */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleArchiveCampaign(campaign); }}
                        title={campaign.archived ? 'Unarchive campaign' : 'Archive campaign'}
                        className="p-1.5 rounded-lg text-white hover:text-white hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-all"
                      >
                        {campaign.archived
                          ? <ArchiveRestore className="w-4 h-4" />
                          : <Archive className="w-4 h-4" />}
                      </button>
                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="text-xs text-white font-medium">View</span>
                        <ChevronRight className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Campaign Modal */}
      <AddCampaignModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          fetchCampaigns();
        }}
      />
    </div>
  );
};

export default Campaigns;